import { CSSTransition } from 'react-transition-group';
import ReactDOM from 'react-dom';
import { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import styles from './style.module.scss';
import zhCountryData from './country-code-data.json';
import enCountryData from './country-code-en-data.json';

interface CountryCodeItem {
  code: string;
  zhName: string;
  enName: string;
  ml?: string;
}

type CountryData = Record<string, Array<CountryCodeItem>>;

export interface CountryCodePanelProps {
  value?: string;
  children?(args: { showPanel(): void; value?: string }): React.ReactElement;
  onChange?(value: CountryCodeItem): void;
  onVisibleChange?(visible: boolean): void;
  language?: 'zh' | 'en';
  hide86?: boolean;
}

const CountryCodePanel = ({
  children,
  value,
  onChange,
  onVisibleChange,
  language,
  hide86 = true,
}: CountryCodePanelProps) => {
  const [innerVisible, setVisible] = useState(false);
  const [touchingBar, setTouchingBar] = useState(false);
  const letterContainerRef = useRef<HTMLDivElement>(null);
  const anchorsRef = useRef<Record<string, HTMLDivElement>>({});
  const codeItemRef = useRef<Record<string, HTMLDivElement>>({});
  const [innerValue, setValue] = useState(value);
  const touchTemp = useRef([0, 0]);

  const [letters, setLetters] = useState(Object.keys(zhCountryData));
  const [countryData, setCountryData] = useState<CountryData>(zhCountryData);

  useEffect(() => {
    setValue(value);
  }, [value]);

  useEffect(() => {
    const enData = enCountryData;
    const zhData = zhCountryData;

    if (hide86) {
      enData.C = enData.C.filter((item) => item.code !== '+86');
      zhData.Z = zhData.Z.filter((item) => item.code !== '+86');
    }

    setLetters(Object.keys(language === 'en' ? enData : zhData));
    setCountryData(language === 'en' ? enData : zhData);
  }, [innerVisible, language, hide86]);

  useEffect(() => {
    setLetters(Object.keys(language === 'en' ? enCountryData : zhCountryData));
    setCountryData(language === 'en' ? enCountryData : zhCountryData);
  }, [innerVisible, language]);

  useEffect(() => {
    onVisibleChange?.(innerVisible);
    document.body.style.overflow = innerVisible ? 'hidden' : '';
  }, [innerVisible]);

  useEffect(() => {
    if (innerVisible && innerValue) {
      codeItemRef.current[innerValue]?.scrollIntoView({ block: 'center' });
    }
  }, [innerVisible]);

  useEffect(() => {
    const handleTouchEnd = () => {
      setTouchingBar(false);
    };

    document.addEventListener('touchend', handleTouchEnd);

    return () => {
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, []);

  const handleStartFindLetter: React.TouchEventHandler<HTMLDivElement> = (e) => {
    setTouchingBar(true);
    const { currentTarget } = e;
    const { top } = currentTarget.getBoundingClientRect();
    const letterDOMs = Array.from(currentTarget.children);
    const itemHeight = letterDOMs.pop()!.clientHeight;
    const paddingTop = parseInt(getComputedStyle(currentTarget).paddingTop, 10);
    touchTemp.current = [top + paddingTop, itemHeight];
  };

  const scrollToAnchorByLetter = async (letter: string) => {
    /**
     * 从下往上定位有问题，目前能通过先移动到顶部再定位
     */
    await anchorsRef.current[language === 'en' ? 'Hot' : '热']?.scrollIntoView();
    anchorsRef.current[letter]?.scrollIntoView();
  };

  const handleTouchMove: React.TouchEventHandler<HTMLDivElement> = (e) => {
    const { touches } = e;

    const [offsetTop, itemHeight] = touchTemp.current;

    const touchOffsetTop = touches[0].clientY - offsetTop;

    if (touchOffsetTop > 0) {
      const index = Math.ceil(touchOffsetTop / itemHeight) - 1;

      scrollToAnchorByLetter(letters[index]);
    }
  };

  const child = children?.({ showPanel: () => setVisible(true), value: innerValue });

  return (
    <>
      {child}
      {globalThis.document
        ? ReactDOM.createPortal(
            <>
              <CSSTransition
                unmountOnExit
                timeout={300}
                in={innerVisible}
                classNames={{
                  enter: styles.fade,
                  enterActive: styles.fadeActive,
                  exit: styles.fadeOut,
                  exitActive: styles.fadeOutActive,
                }}
              >
                <div className={styles.mask} onClick={() => setVisible(false)} />
              </CSSTransition>
              <CSSTransition
                unmountOnExit
                timeout={300}
                in={innerVisible}
                classNames={{
                  enter: styles.slideIn,
                  enterActive: styles.slideInActive,
                  exitActive: styles.slideOutActive,
                }}
              >
                <div className={styles.panel}>
                  <svg
                    width="20px"
                    height="20px"
                    viewBox="0 0 20 20"
                    version="1.1"
                    xmlns="http://www.w3.org/2000/svg"
                    xmlnsXlink="http://www.w3.org/1999/xlink"
                    className={styles.close}
                    onClick={() => setVisible(false)}
                  >
                    <g stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
                      <g transform="translate(-343.000000, -100.000000)">
                        <g transform="translate(343.000000, 100.000000)">
                          <circle fill="#E6E6E6" cx="10" cy="10" r="10" />
                          <g
                            transform="translate(10.000000, 10.000000) rotate(-315.000000) translate(-10.000000, -10.000000) translate(4.000000, 4.000000)"
                            fill="#FFFFFF"
                          >
                            <rect x="5" y="0" width="2" height="12" rx="1" />
                            <rect
                              transform="translate(6.000000, 6.000000) rotate(-270.000000) translate(-6.000000, -6.000000) "
                              x="5"
                              y="0"
                              width="2"
                              height="12"
                              rx="1"
                            />
                          </g>
                        </g>
                      </g>
                    </g>
                  </svg>
                  <div className={classNames(styles.countryCodes, touchingBar && styles.touchingStatic)}>
                    {letters.map((letter, i) => (
                      <div key={i} className={styles.codeGroup}>
                        <div
                          className={styles.letter}
                          tabIndex={-1}
                          ref={(dom) => {
                            if (dom) {
                              anchorsRef.current[letter] = dom;
                            }
                          }}
                        >
                          {language === 'en' ? (
                            <span>{letter === 'Hot' ? 'Hot' : letter}</span>
                          ) : (
                            <span>{letter === '热' ? '热门' : letter}</span>
                          )}
                        </div>
                        {(countryData as CountryData)[letter].map((item, i) => (
                          <div
                            className={classNames(styles.codeItem, innerValue === item.code && styles.selected)}
                            key={i}
                            onClick={() => {
                              setValue(item.code);
                              onChange?.(item);
                              setVisible(false);
                            }}
                            ref={(codeDom) => {
                              if (codeDom) {
                                codeItemRef.current[item.code] = codeDom;
                              }
                            }}
                          >
                            {language && language === 'en' ? item.enName : item.zhName}
                            <div className={styles.code}>{item.code}</div>
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                  <div
                    className={classNames(styles.touchBar, touchingBar && styles.touching)}
                    onTouchStart={handleStartFindLetter}
                    onTouchMove={handleTouchMove}
                    onContextMenu={(e) => {
                      e.preventDefault();
                    }}
                    ref={letterContainerRef}
                  >
                    {letters.map((letter) => (
                      <div
                        key={letter}
                        onClick={() => {
                          setTimeout(() => {
                            scrollToAnchorByLetter(letter);
                          });
                        }}
                        onTouchStart={() => {
                          setTimeout(() => {
                            scrollToAnchorByLetter(letter);
                          });
                        }}
                      >
                        {letter}
                      </div>
                    ))}
                  </div>
                </div>
              </CSSTransition>
            </>,
            document.body,
          )
        : null}
    </>
  );
};

export default CountryCodePanel;
